import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const QuickReference = ({ producers, animals, isMobile }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  useEffect(() => {
    // console.log('QuickReference mounted/updated:', { producers, animals });
  }, [producers, animals]);

  const hasProducer1 = producers?.length > 0 && producers[0]?.Name;
  
  if (!hasProducer1) {
    // console.log('QuickReference not showing: no producer 1');
    return null;
  }

  // Mobile collapsible version
  if (isMobile) {
    return (
      <div className="fixed bottom-0 left-0 right-0 w-full bg-gradient-to-br from-gray-950/90 to-blue-950/80 border-t border-blue-800/30 shadow-lg z-[9999]"
        style={{
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.2)',
        }}
      >
        <motion.div 
          className="px-4 py-3 flex justify-between items-center"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 flex items-center justify-center bg-white/10 rounded-lg text-white text-sm">
              📋
            </span>
            <h3 className="text-sm font-medium text-white">Quick Reference</h3>
            <span className="text-xs text-white/50 ml-2">{isExpanded ? 'Tap to collapse' : 'Tap to expand'}</span>
          </div>
          <motion.button 
            className="w-6 h-6 flex items-center justify-center bg-white/10 rounded-lg text-white"
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            ▲
          </motion.button>
        </motion.div>
        
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-3 max-h-[35vh] overflow-y-auto">
                {/* Producers Section */}
                <div className="bg-black/20 rounded-xl p-3">
                  <h4 className="text-xs font-medium text-white mb-2.5 flex items-center gap-1.5">
                    <span className="text-sm">🌿</span>
                    Producers
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    {producers.map((producer, index) => (
                      producer.Name && (
                        <motion.div
                          key={producer.id}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center text-xs bg-white/10 rounded-lg p-2 hover:bg-white/20 transition-colors"
                        >
                          <span className="w-4 h-4 flex items-center justify-center bg-white/20 rounded-md text-white font-mono border border-white/20 text-[10px]">
                            {index + 1}
                          </span>
                          <span className="ml-1.5 text-white/90 truncate">{producer.Name}</span>
                        </motion.div>
                      )
                    ))}
                  </div>
                </div>

                {/* Animals Section */}
                <div className="bg-black/20 rounded-xl p-3">
                  <h4 className="text-xs font-medium text-white mb-2.5 flex items-center gap-1.5">
                    <span className="text-sm">🦁</span>
                    Animals
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {animals.map((animal, index) => (
                      animal.Name && (
                        <motion.div
                          key={animal.id}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center text-xs bg-white/10 rounded-lg p-2 hover:bg-white/20 transition-colors"
                        >
                          <span className="w-4 h-4 flex items-center justify-center bg-white/20 rounded-md text-white font-mono border border-white/20 text-[10px]">
                            {String.fromCharCode(65 + index)}
                          </span>
                          <span className="ml-1.5 text-white/90 truncate">{animal.Name}</span>
                        </motion.div>
                      )
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Desktop version (unchanged)
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="fixed right-6 top-24 w-64 bg-gradient-to-br from-gray-950/80 to-blue-950/60 rounded-xl border border-blue-800/20 p-4 shadow-xl z-[9999]"
      style={{
        position: 'fixed',
        right: '1.5rem',
        top: '6rem',
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(0, 0, 0, 0.2)',
      }}
    >
      <div className="relative">
        {/* Title Section */}
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg text-white">
            📋
          </span>
          Quick Reference
          <div className="text-xs text-white/70 font-normal mt-1">
            No need to scroll - all your entries are here!
          </div>
        </h3>

        {/* Producers Section */}
        <div className="mb-6 bg-black/20 rounded-lg p-3">
          <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
            <span className="text-lg">🌿</span>
            Producers
          </h4>
          <div className="space-y-2">
            {producers.map((producer, index) => (
              producer.Name && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  key={producer.id}
                  className="flex items-center text-sm bg-white/10 rounded-lg p-2 hover:bg-white/20 transition-colors"
                >
                  <span className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-lg text-white font-mono border border-white/20">
                    {index + 1}
                  </span>
                  <span className="ml-2 text-white/90">{producer.Name}</span>
                </motion.div>
              )
            ))}
          </div>
        </div>

        {/* Animals Section */}
        <div className="bg-black/20 rounded-lg p-3">
          <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
            <span className="text-lg">🦁</span>
            Animals
          </h4>
          <div className="space-y-2">
            {animals.map((animal, index) => (
              animal.Name && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  key={animal.id}
                  className="flex items-center text-sm bg-white/10 rounded-lg p-2 hover:bg-white/20 transition-colors"
                >
                  <span className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-lg text-white font-mono border border-white/20">
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span className="ml-2 text-white/90">{animal.Name}</span>
                </motion.div>
              )
            ))}
          </div>
        </div>

        {/* Decorative bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
      </div>
    </motion.div>
  );
};

export default QuickReference; 